import { QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';

import { AuthProvider } from '@/features/auth/AuthProvider';
import { NotificationSocketProvider } from '@/features/notifications/NotificationSocketProvider';
import i18n from '@/i18n';
import { ErrorBoundary } from '@/shared/components/ErrorBoundary';

import { queryClient } from './queryClient';

/**
 * Provider order matters: the boundary is outermost so it catches failures in
 * everything below it, and Auth sits inside Query because session restore is
 * itself an HTTP call. The notification socket sits inside Auth because its
 * handshake needs an authenticated client — and it must be one socket per
 * session, not one per bell render (see NotificationSocketProvider).
 */
export function Providers({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <NotificationSocketProvider>{children}</NotificationSocketProvider>
          </AuthProvider>
        </QueryClientProvider>
      </I18nextProvider>
    </ErrorBoundary>
  );
}
